import { Print, Search, SwapHoriz } from "@mui/icons-material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { quickReasons } from "../../config/config";
import { Customer } from "../../data/models/customers/CustomerModel";
import { ModuleModel } from "../../data/models/modules/ModuleModel";
import {
  TypeDiscountEnum,
  TypePaymentMethodEnum,
} from "../../data/models/order/PaymentModel";
import { Product } from "../../data/models/product/ProductModel";
import { useUserData } from "../../data/providers/UserDataProvider";
import { toMillis } from "../../utils/StringTool";
import { useModuleActions } from "../hooks/useModuleActions";
import { OrderItem, useModuleOrder } from "../hooks/useModuleOrder";
import { useInsideDialogClick } from "../hooks/userClickHandler";
import { ChangeModuleDialog } from "./components/ChangeModuleDialog";
import { CheckoutDialog } from "./components/CheckoutDialog";
import { InvoicePrint, InvoicePrintProps } from "./components/InvoicePrint";

export default function ModuleOrderPage() {
  const navigate = useNavigate();

  const {
    items,
    addProduct,
    // updateQuantity,
    removeProduct,
    total,
    selectedCustomer,
    setSelectedCustomer,
    discountType,
    setDiscountType,
    discountValue,
    setDiscountValue,
    subtotal,
    openDiscount,
    setOpenDiscount,
    payment,
    setPayment,
    moduleId,
    orderData,
    loadingOrder,
    errorOrder,
    products,
    categories,
    customers,
    assignCustomer,
    showButtonPreparation,
    sendToPreparation,
    cashesOpened,
    handlePayOrder,
    showError,
  } = useModuleOrder();

  const userData = useUserData()!;

  const discountRef = useInsideDialogClick(() => setOpenDiscount(false));
  const [openCheckout, setOpenCheckout] = useState(false);
  const [receivedAmount, setReceivedAmount] = useState<number>(0);
  const [reference, setReference] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [qty, setQty] = useState(1);
  const [comment, setComment] = useState("");
  const [dataToPrint, setDataToPrint] = useState<InvoicePrintProps | null>(
    null
  );
  const [printing, setPrinting] = useState(false);
  const [openReasonDialog, setOpenReasonDialog] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [reason, setReason] = useState("");

  const [search, setSearch] = useState("");
  const [showCustomerSearch, setShowCustomerSearch] = useState(false);

  const { migrateOrderToModule, getAvailableModules } = useModuleActions();
  const [availableModules, setAvailableModules] = useState<ModuleModel[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  const companyCode = userData.data.currentCompany.companyCode;
  const storeCode = userData.data.currentStore.storeCode;

  const filteredProducts = products.filter((p: Product) =>
    `${p.name}`.toLowerCase().includes(search.toLowerCase())
  );

  const handleBack = () => navigate("/dashboard/tables");

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((w) => w[0])
      .join("")
      .substring(0, 2)
      .toUpperCase();
  };

  useEffect(() => {
    if (errorOrder) {
      handleBack();
    }
  }, [errorOrder]);

  const contentRef = useRef<HTMLDivElement>(null);

  const resetDialog = () => {
    setQty(1);
    setComment("");
    setSelectedProduct(null);
  };

  const printInSameWindow = (goBack?: boolean) => {
    const source = contentRef.current;
    const preview = document.getElementById("printPreview");

    if (!source || !preview) return;

    setPrinting(true);

    preview.innerHTML = source.innerHTML;
    preview.style.display = "block";

    setTimeout(() => {
      window.print();
      setTimeout(() => {
        setPrinting(false);
        if (goBack) handleBack();
      }, 800);
    }, 300);
  };

  useEffect(() => {
    if (!dataToPrint) return;

    printInSameWindow(dataToPrint.printMode !== "PRE_INVOICE");
  }, [dataToPrint]);

  const buildInvoicePrintData = (
    printMode: "ORIGINAL" | "COPY" | "PRE_INVOICE",
    fiscalNumber?: string
  ): InvoicePrintProps | null => {
    if (!orderData) return null;
    const company = userData?.data.currentCompany;

    const companyInfo = {
      name: company.companyName,
      nit: company.identification,
      address: company.address,
      phone: company.phoneNumber,
      logoUrl: company.imageUrl,
    };

    const customer = selectedCustomer;

    const dateObj = new Date(orderData.createdAt);
    const date = dateObj.toLocaleDateString("es-CO");
    const hour = dateObj.toLocaleTimeString("es-CO", {
      hour: "2-digit",
      minute: "2-digit",
    });

    return {
      orderId: orderData.id,
      items: items.map((i) => ({
        name: i.product.name,
        qty: i.quantity,
        price: i.product.salePrice,
        comments: i.comments,
      })),
      subtotal: subtotal,
      discount: discountValue,
      total: total,
      company: companyInfo,
      customer: customer
        ? {
            name: customer.fullName,
            phone: customer.phone,
            address: customer.address,
          }
        : {},
      module: orderData.moduleName!,
      date,
      hour,
      paymentMethod: payment ?? orderData.paymentMethod ?? null,
      paidAmount: null,
      change: null,
      fiscalNumber,
      printMode,
    };
  };

  const ProductCard = ({ p, onClick }: { p: Product; onClick: () => void }) => {
    return (
      <Box
        onClick={onClick}
        sx={{
          borderRadius: "20px",
          padding: 2,
          border: "1px solid",
          borderColor: "divider",
          cursor: "pointer",
          transition: "0.2s",
          background: "primary.main",
          "&:hover": {
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            transform: "translateY(-3px)",
          },
        }}
      >
        <Box
          sx={{
            height: 60,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderColor: "divider",
            backgroundColor: "divider",
            borderRadius: "14px",
            mb: 1.5,
          }}
        >
          <Typography fontSize={22} fontWeight="700">
            {getInitials(p.name)}
          </Typography>
        </Box>

        <Typography fontWeight={600} sx={{ fontSize: "14px" }}>
          {p.name}
        </Typography>
        <Typography color="text.secondary" sx={{ fontSize: "13px" }}>
          ${p.salePrice}
        </Typography>
      </Box>
    );
  };

  const handleDeleteClick = (id: string) => {
    if (orderData?.hasStation) {
      setSelectedItemId(id);
      setOpenReasonDialog(true);
    } else {
      removeProduct(id, "");
    }
  };

  const handleConfirmDelete = () => {
    if (selectedItemId) {
      removeProduct(selectedItemId, reason);
    }
    setReason("");
    setSelectedItemId(null);
    setOpenReasonDialog(false);
  };

  const handleConfirmCheckout = async ({
    paymentMethod,
    reference,
    discountType,
    discountValue,
    cashId,
    withPrint,
  }: {
    paymentMethod: TypePaymentMethodEnum;
    reference?: string;
    discountType: TypeDiscountEnum;
    discountValue: number;
    cashId: string;
    withPrint?: boolean;
  }) => {
    if (!cashId) {
      showError("Debes seleccionar una caja abierta.");
      return;
    }

    if (!paymentMethod) {
      showError("Debes seleccionar un método de pago.");
      return;
    }

    const hasDiscount = discountValue > 0;

    if (hasDiscount) {
      if (!discountType) {
        showError("Debes seleccionar el tipo de descuento.");
        return;
      }
      if (discountValue == null || discountValue <= 0) {
        showError("Debes ingresar un valor de descuento válido.");
        return;
      }
    }
    try {
      const r = await handlePayOrder({
        cashId,
        paymentMethod,
        discountType,
        discountValue,
      });
      if (!r) throw new Error("new errors");
      setOpenCheckout(false);
      if (withPrint) {
        setDataToPrint(buildInvoicePrintData("ORIGINAL", r.fiscalNumber));
      } else {
        handleBack();
      }
    } catch (error) {
      showError("Ocurrió un error al facturar.");
      return;
    }
  };

  const handleOpenChangeModule = async () => {
    setAvailableModules(null as any);
    setDialogOpen(true);
    const modules = await getAvailableModules({ companyCode, storeCode });
    modules.sort((a, b) => {
      const da = toMillis(a.createdAt);
      const db = toMillis(b.createdAt);
      return da - db;
    });
    setAvailableModules(modules);
  };

  const handleSelectNewModule = async (targetModuleId: string) => {
    await migrateOrderToModule({
      currentModuleId: moduleId!,
      targetModuleId,
      orderId: orderData!.id,
      userName: userData.data.fullName,
    });
    setDialogOpen(false);
    sendToPreparation();
    navigate(`/dashboard/tables/orders?moduleId=${targetModuleId}`, {
      replace: true,
    });
    navigate(0);
  };

  return (
    <Stack sx={{ mb: 2 }}>
      <Box sx={{ height: 4 }}>
        {loadingOrder && <LinearProgress sx={{ height: 4 }} />}
      </Box>
      <Box sx={{ p: 2, width: "100%", height: "100%" }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{
              borderRadius: "12px",
              borderColor: "divider",
              color: "primary.main",
              "&:hover": {
                borderColor: "secondary.main",
              },
            }}
          >
            Volver
          </Button>

          <Typography variant="h5" fontWeight={700} fontSize={18}>
            {orderData?.moduleName}
          </Typography>
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Stack gap={2}>
              <Stack direction="row" gap={1}>
                <TextField
                  placeholder="Buscar productos"
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                    },
                    borderColor: "divider",
                  }}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Stack>

              <Stack gap={3}>
                {filteredProducts.length === 0 && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        textAlign: "center",
                        py: 5,
                        color: "text.secondary",
                        fontSize: "18px",
                        borderRadius: "16px",
                        border: "1px dashed #ccc",
                      }}
                    >
                      No hay productos para mostrar
                    </Box>
                  </Grid>
                )}

                {categories
                  .filter((cat) => {
                    if (search.trim().length === 0) {
                      return cat.name === "PAPAS CRUNCHY";
                    }
                    return true;
                  })
                  .map((cat) => {
                    const productsInCategory = filteredProducts.filter(
                      (p) => p.categoryId === cat.id
                    );
                    if (productsInCategory.length === 0) return null;
                    return (
                      <Box key={cat.id}>
                        <Typography
                          variant="h6"
                          fontSize={16}
                          fontWeight={700}
                          sx={{ mb: 1.5 }}
                        >
                          {cat.name}
                        </Typography>
                        <Grid container spacing={2}>
                          {productsInCategory.map((p) => (
                            <Grid item xs={6} sm={4} md={3} key={p.id}>
                              <ProductCard
                                p={p}
                                onClick={() => {
                                  setSelectedProduct(p);
                                  setComment("");
                                  setQty(1);
                                  setOpenDialog(true);
                                }}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    );
                  })}
              </Stack>
            </Stack>
          </Grid>

          <Grid item xs={12} md={4}>
            <Stack
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "20px",
                p: 2,
                height: "100%",
              }}
              spacing={2}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="flex-end"
                justifyContent="flex-end"
              >
                <Button
                  variant="outlined"
                  onClick={() => setShowCustomerSearch(!showCustomerSearch)}
                  sx={{
                    minHeight: 25,
                    borderRadius: "14px",
                    textTransform: "none",
                    borderColor: "#6c8cff",
                    color: "#6c8cff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "&:hover": {
                      borderColor: "#4a6fff",
                    },
                  }}
                >
                  <Search sx={{ fontSize: "20px" }} />
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    requestAnimationFrame(() => {
                      const invoice = buildInvoicePrintData("PRE_INVOICE");
                      setDataToPrint(invoice);
                    });
                  }}
                  sx={{
                    minHeight: 25,
                    borderRadius: "14px",
                    textTransform: "none",
                    borderColor: "#ff8b8b",
                    color: "#d94848",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "&:hover": {
                      borderColor: "#ff6b6b",
                    },
                  }}
                >
                  <Print sx={{ fontSize: "20px" }} />
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleOpenChangeModule}
                  sx={{
                    minHeight: 25,
                    borderRadius: "14px",
                    borderColor: "#4a6fff",
                    textTransform: "none",
                    color: "#4a6fff",
                    px: 1.8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "&:hover": {
                      backgroundColor: "#6281ff25",
                    },
                  }}
                >
                  <SwapHoriz sx={{ fontSize: 20, mr: 0.3 }} />
                </Button>
              </Stack>
              {showCustomerSearch && (
                <Autocomplete
                  options={customers}
                  getOptionLabel={(option: Customer) => option.fullName}
                  value={selectedCustomer}
                  onChange={(event, newValue) => {
                    if (!newValue) return;
                    setSelectedCustomer(newValue);
                    setShowCustomerSearch(false);
                    assignCustomer({
                      orderId: orderData?.id!,
                      customerId: newValue.id!,
                    });
                  }}
                  sx={{ mt: 2 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Buscar cliente"
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: "12px" },
                      }}
                    />
                  )}
                />
              )}
              <Box sx={{ mt: 2 }}>
                {orderData ? (
                  <Typography sx={{ fontSize: 14 }}>
                    Orden: {orderData.id}
                  </Typography>
                ) : (
                  <Skeleton width={120} height={28} />
                )}
              </Box>
              {loadingOrder && !selectedCustomer ? (
                <Box sx={{ mt: 2 }}>
                  <Skeleton
                    variant="text"
                    animation="wave"
                    width={200}
                    height={26}
                  />
                  <Skeleton
                    variant="text"
                    animation="wave"
                    width={140}
                    height={20}
                  />
                  <Skeleton
                    variant="text"
                    animation="wave"
                    width={170}
                    height={20}
                  />
                  <Skeleton
                    variant="text"
                    animation="wave"
                    width={150}
                    height={20}
                  />
                </Box>
              ) : selectedCustomer ? (
                <Box sx={{ mt: 2 }}>
                  <Typography fontWeight={700}>
                    Cliente: {selectedCustomer.fullName}
                  </Typography>

                  {selectedCustomer.phone && (
                    <Typography color="text.secondary" fontSize={14}>
                      📞 {selectedCustomer.phone}
                    </Typography>
                  )}

                  {selectedCustomer.email && (
                    <Typography color="text.secondary" fontSize={14}>
                      ✉️ {selectedCustomer.email}
                    </Typography>
                  )}

                  {selectedCustomer.documentNumber && (
                    <Typography color="text.secondary" fontSize={14}>
                      🆔 {selectedCustomer.documentNumber}
                    </Typography>
                  )}
                </Box>
              ) : null}

              <Divider />
              <Stack gap={2} sx={{ maxHeight: 300, overflowY: "auto" }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{ color: "text.primary", mb: 0 }}
                >
                  Productos agregados
                </Typography>
                {items.length === 0 ? (
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 4,
                      border: "1px dashed",
                      borderColor: "divider",
                      borderRadius: "12px",
                      color: "text.secondary",
                    }}
                  >
                    <Typography fontSize={16} fontWeight={600} sx={{ mb: 1 }}>
                      No hay productos en la orden
                    </Typography>
                    <Typography fontSize={14}>
                      Agrega productos tocando las tarjetas del catálogo
                    </Typography>
                  </Box>
                ) : (
                  items.map((i: OrderItem) => (
                    <Stack
                      key={i.id}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{
                        borderBottom: "1px dashed",
                        borderColor: "divider",
                        pb: 1,
                      }}
                    >
                      <Box>
                        <Typography fontWeight={600}>
                          {i.product.name}
                        </Typography>
                        <Typography color="text.secondary">
                          ${i.product.salePrice}
                        </Typography>
                        {Boolean(i.comments) && (
                          <Typography
                            color="text.secondary"
                            sx={{
                              backgroundColor: "rgba(255, 0, 0, 0.2)",
                              padding: "2px 8px",
                              borderRadius: "8px",
                              fontSize: "12px",
                              fontWeight: "bold",
                            }}
                          >
                            {i.comments}
                          </Typography>
                        )}
                      </Box>
                      <Stack direction="row" alignItems="center" gap={1}>
                        <Typography
                          sx={{
                            minWidth: 24,
                            textAlign: "center",
                            fontWeight: 600,
                          }}
                        >
                          {"x " + i.quantity}
                        </Typography>
                        {/* <IconButton
                          onClick={() =>
                            updateQuantity(
                              i.product.id!,
                              Math.max(1, i.quantity - 1)
                            )
                          }
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: "8px",
                            width: 32,
                            height: 32,
                          }}
                        >
                          <Typography fontWeight={700}>−</Typography>
                        </IconButton>                      
                        <IconButton
                          onClick={() =>
                            updateQuantity(i.product.id!, i.quantity + 1)
                          }
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: "8px",
                            width: 32,
                            height: 32,
                          }}
                        >
                          <Typography fontWeight={700}>+</Typography>
                        </IconButton> */}
                        {(!orderData?.hasStation ||
                          userData?.data.role.includes("ADMIN")) && (
                          <IconButton onClick={() => handleDeleteClick(i.id!)}>
                            <DeleteIcon color="error" />
                          </IconButton>
                        )}
                      </Stack>
                    </Stack>
                  ))
                )}
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography fontWeight={600}>Subtotal:</Typography>
                <Typography fontWeight={600}>
                  ${subtotal.toLocaleString()}
                </Typography>
              </Stack>

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mt: 1.5 }}
              >
                <Typography fontWeight={700} fontSize={20}>
                  Total:
                </Typography>
                <Typography fontWeight={700} fontSize={20}>
                  ${total.toLocaleString()}
                </Typography>
              </Stack>
              <Stack>
                <Divider sx={{ my: 2 }} />
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => sendToPreparation()}
                  sx={{
                    borderRadius: "12px",
                    background: "primary",
                    fontWeight: "600",
                  }}
                  disabled={loadingOrder || orderData?.hasStation}
                >
                  Enviar a preparación
                </Button>
              </Stack>
              <Divider sx={{ my: 2 }} />
              <Button
                variant="contained"
                size="large"
                sx={{
                  borderRadius: "12px",
                  fontWeight: "600",
                }}
                disabled={items.length === 0}
                onClick={() => {
                  setPayment(TypePaymentMethodEnum.CASH);
                  setReceivedAmount(0);
                  setReference("");
                  setOpenCheckout(true);
                }}
              >
                FACTURAR
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            p: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: "20px",
            pb: 0,
          }}
        >
          {selectedProduct?.name}{" "}
        </DialogTitle>

        <DialogContent sx={{ mt: 1 }}>
          <Stack gap={2}>
            {selectedProduct?.suggestions &&
              selectedProduct?.suggestions?.length > 0 && (
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  gap={1}
                  sx={{ marginY: 0 }}
                >
                  {selectedProduct!.suggestions.map((sug) => (
                    <Box
                      key={sug}
                      onClick={() => {
                        setComment((prev) => {
                          const trimmed = prev.trim();
                          if (trimmed.includes(sug))
                            return trimmed.endsWith("|")
                              ? trimmed
                              : trimmed + " |";
                          if (!trimmed) return `${sug} |`;
                          return `${trimmed.replace(/\|?$/, "")} | ${sug} |`;
                        });
                      }}
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "12px",
                        bgcolor: "#e8f0fe",
                        border: "1px solid #90caf9",
                        color: "#1e88e5",
                        fontSize: "12px",
                        fontWeight: 600,
                        cursor: "pointer",
                        userSelect: "none",
                        transition: "0.2s",
                        "&:hover": {
                          bgcolor: "#d0e3fc",
                        },
                      }}
                    >
                      {sug}
                    </Box>
                  ))}
                </Stack>
              )}
            <TextField
              label="Comentarios (opcional)"
              multiline
              rows={2}
              fullWidth
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
              }}
            />
            <Stack alignItems="center" gap={1.5}>
              <Typography fontWeight={600}>Cantidad</Typography>
              <Stack direction="row" gap={2} alignItems="center">
                <Button
                  variant="contained"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  sx={{
                    minWidth: 50,
                    height: 50,
                    borderRadius: "12px",
                    fontSize: "22px",
                    fontWeight: 700,
                  }}
                >
                  –
                </Button>
                <TextField
                  value={qty}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    setQty(Number.isNaN(val) ? 1 : Math.max(1, val));
                  }}
                  inputProps={{
                    style: {
                      textAlign: "center",
                      width: 70,
                      fontSize: "20px",
                      fontWeight: 700,
                    },
                    type: "number",
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      height: "50px",
                      fontSize: "22px",
                    },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={() => setQty((q) => q + 1)}
                  sx={{
                    minWidth: 50,
                    height: 50,
                    borderRadius: "12px",
                    fontSize: "22px",
                    fontWeight: 700,
                  }}
                >
                  +
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 2,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button
            onClick={() => setOpenDialog(false)}
            sx={{
              flex: 1,
              mr: 1,
              borderRadius: "12px",
              height: 48,
              textTransform: "none",
              fontWeight: 600,
            }}
            variant="outlined"
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={async () => {
              setOpenDialog(false);
              resetDialog();
              await addProduct(selectedProduct!, qty, comment);
            }}
            loading={loadingOrder}
            sx={{
              flex: 1,
              ml: 1,
              borderRadius: "12px",
              height: 48,
              textTransform: "none",
              fontWeight: 700,
            }}
          >
            Agregar
          </Button>
        </DialogActions>
      </Dialog>

      <CheckoutDialog
        open={openCheckout}
        onClose={() => setOpenCheckout(false)}
        subtotal={subtotal}
        total={total}
        discountValue={discountValue}
        discountType={discountType}
        setDiscountValue={setDiscountValue}
        setDiscountType={setDiscountType}
        discountRef={discountRef}
        cashesOpened={cashesOpened ?? []}
        onConfirm={handleConfirmCheckout}
      />

      <Dialog
        open={openReasonDialog}
        onClose={() => setOpenReasonDialog(false)}
      >
        <DialogTitle>Motivo de eliminación</DialogTitle>

        <DialogContent>
          <Stack spacing={2}>
            <Stack
              direction="row"
              justifyContent={"center"}
              spacing={1}
              flexWrap="wrap"
              sx={{ gap: 1, mb: 2 }}
            >
              {quickReasons.map((r) => (
                <Chip
                  key={r}
                  label={r}
                  onClick={() => setReason(r)}
                  sx={{
                    cursor: "pointer",
                    mb: 1,
                    borderRadius: "12px",
                    px: 1.5,
                    fontWeight: 600,
                    bgcolor: reason === r ? "primary.main" : "rgba(0,0,0,0.05)",
                    color: reason === r ? "white" : "text.primary",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor:
                        reason === r ? "primary.dark" : "rgba(0,0,0,0.1)",
                    },
                  }}
                />
              ))}
            </Stack>

            <TextField
              label="Otro motivo"
              multiline
              minRows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Escribe un motivo..."
              fullWidth
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenReasonDialog(false)}>Cancelar</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!reason}
            onClick={handleConfirmDelete}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <ChangeModuleDialog
        open={dialogOpen}
        modules={availableModules}
        onSelect={handleSelectNewModule}
        onClose={() => setDialogOpen(false)}
      />

      <div style={{ display: "none" }}>
        {dataToPrint && <InvoicePrint ref={contentRef} {...dataToPrint} />}
      </div>

      {printing && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            bgcolor: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(2px)",
            pointerEvents: "auto",
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <LinearProgress sx={{ width: 220, height: 6, borderRadius: 3 }} />
            <Typography color="white" fontWeight={600}>
              Modo impresión...
            </Typography>
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
